/**
 * LINE ニュース配信予約ダイアログ。
 * 仕様書: docs/01_spec/spec_line_bot.md A-10.5（Phase 22h）
 */
import { CalendarClock, Globe, Users } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useScheduleLineNewsSend } from '@/hooks/useLineNews';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newsId: string;
  newsTitle: string;
  onScheduled?: () => void;
}

const MIN_LEAD_MINUTES = 1;

function toLocalInput(value: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
}

function defaultSendAt(): string {
  const d = new Date(Date.now() + 5 * 60 * 1000);
  d.setSeconds(0, 0);
  return toLocalInput(d);
}

export function ScheduleNewsDialog({
  open,
  onOpenChange,
  newsId,
  newsTitle,
  onScheduled,
}: Props) {
  const scheduleMutation = useScheduleLineNewsSend();
  const [targetType, setTargetType] = useState<'broadcast' | 'multicast'>('broadcast');
  const [userIdsRaw, setUserIdsRaw] = useState('');
  const [sendAt, setSendAt] = useState(defaultSendAt());

  useEffect(() => {
    if (open) {
      setTargetType('broadcast');
      setUserIdsRaw('');
      setSendAt(defaultSendAt());
    }
  }, [open]);

  const userIds =
    targetType === 'multicast'
      ? userIdsRaw
          .split(/[\s,;\n]+/)
          .map((s) => s.trim())
          .filter((s) => s.length > 0)
      : [];

  const handleSubmit = async () => {
    const sendAtMs = new Date(sendAt).getTime();
    if (Number.isNaN(sendAtMs)) {
      toast.error('送信日時が不正です');
      return;
    }
    if (sendAtMs < Date.now() + MIN_LEAD_MINUTES * 60 * 1000) {
      toast.error(`送信日時は現在から ${MIN_LEAD_MINUTES} 分以降を指定してください`);
      return;
    }
    if (targetType === 'multicast' && userIds.length === 0) {
      toast.error('送信先 user ID を 1 件以上入力してください');
      return;
    }
    try {
      const res = await scheduleMutation.mutateAsync({
        newsId,
        sendAt: new Date(sendAt).toISOString(),
        targetUserIds: userIds,
      });
      const target =
        userIds.length === 0
          ? '全友だち（Broadcast）'
          : `${res.recipientCount} 名`;
      toast.success(
        `予約しました（${target}、${new Date(sendAt).toLocaleString('ja-JP', { hour12: false })}）`,
      );
      onScheduled?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(
        `予約失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  const minSendAt = toLocalInput(
    new Date(Date.now() + MIN_LEAD_MINUTES * 60 * 1000),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>LINE ニュース配信予約</DialogTitle>
          <DialogDescription>
            「{newsTitle || '無題'}」の配信を予約します。予約時刻に LINE Messaging
            API で自動送信されます（1 分粒度）。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">配信タイプ</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setTargetType('broadcast')}
                className={
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm text-xs border transition-colors ' +
                  (targetType === 'broadcast'
                    ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                    : 'border-[var(--border)] hover:border-[var(--text-muted)]')
                }
              >
                <Globe className="size-3.5" />
                全友だち（Broadcast）
              </button>
              <button
                type="button"
                onClick={() => setTargetType('multicast')}
                className={
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm text-xs border transition-colors ' +
                  (targetType === 'multicast'
                    ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                    : 'border-[var(--border)] hover:border-[var(--text-muted)]')
                }
              >
                <Users className="size-3.5" />
                指定ユーザー（Multicast）
              </button>
            </div>
          </div>

          {targetType === 'multicast' && (
            <div>
              <Label className="text-xs">
                送信先 LINE user ID（カンマ・改行区切り、1 件以上）
              </Label>
              <Textarea
                rows={3}
                placeholder="U123abc..., U456def..."
                value={userIdsRaw}
                onChange={(e) => setUserIdsRaw(e.target.value)}
              />
              <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                {userIds.length} 件入力済み
              </p>
            </div>
          )}

          <div>
            <Label className="text-xs">
              送信日時（{MIN_LEAD_MINUTES} 分以降、現在 ={' '}
              {new Date().toLocaleString('ja-JP', { hour12: false })}）
            </Label>
            <div className="relative">
              <CalendarClock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[var(--text-muted)] pointer-events-none" />
              <Input
                type="datetime-local"
                value={sendAt}
                onChange={(e) => setSendAt(e.target.value)}
                min={minSendAt}
                className="pl-9"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={scheduleMutation.isPending}
          >
            {scheduleMutation.isPending ? (
              <Spinner className="size-3 mr-1" />
            ) : (
              <CalendarClock className="size-4 mr-1" />
            )}
            予約する
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
